import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'microsoft-onedrive',
  version: '1.0.0',
  properties: [
    // === Authentication ===
    {
      key: 'useCredential',
      label: 'Use OAuth2 Credential',
      type: 'boolean',
      default: true,
      description: 'Use stored OAuth2 credential for user-level access to OneDrive'
    },
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'microsoft_onedrive_oauth',
      },
      displayOptions: {
        show: {
          useCredential: [true]
        }
      },
      description: 'Select Microsoft OneDrive OAuth2 credential'
    },

    // === Resource Selection ===
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'file',
      options: [
        { value: 'file', name: 'File', description: 'Work with files in OneDrive' },
        { value: 'folder', name: 'Folder', description: 'Work with folders in OneDrive' }
      ]
    },

    // === FILE Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'download',
      displayOptions: {
        show: {
          resource: ['file']
        }
      },
      options: [
        { value: 'copy', name: 'Copy', description: 'Copy a file to another location' },
        { value: 'delete', name: 'Delete', description: 'Delete a file' },
        { value: 'download', name: 'Download', description: 'Download a file' },
        { value: 'get', name: 'Get', description: 'Get file metadata' },
        { value: 'rename', name: 'Rename', description: 'Rename a file' },
        { value: 'search', name: 'Search', description: 'Search for files' },
        { value: 'share', name: 'Share', description: 'Create a sharing link' },
        { value: 'upload', name: 'Upload', description: 'Upload a new file' }
      ]
    },

    // === FOLDER Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getChildren',
      displayOptions: {
        show: {
          resource: ['folder']
        }
      },
      options: [
        { value: 'create', name: 'Create', description: 'Create a new folder' },
        { value: 'delete', name: 'Delete', description: 'Delete a folder' },
        { value: 'getChildren', name: 'Get Children', description: 'List items in a folder' },
        { value: 'rename', name: 'Rename', description: 'Rename a folder' },
        { value: 'search', name: 'Search', description: 'Search for folders' },
        { value: 'share', name: 'Share', description: 'Create a sharing link' }
      ]
    },

    // ===============================
    // FILE PROPERTIES
    // ===============================

    // Folder selection for file operations (to browse files from)
    {
      key: 'browseFolderId',
      label: 'Folder',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['copy', 'delete', 'download', 'get', 'rename', 'share']
        }
      },
      placeholder: 'Select a folder (optional)',
      description: 'Select a folder to browse files from. Leave empty for root folder.',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // File ID (dynamic dropdown cascading from folder)
    {
      key: 'fileId',
      label: 'File',
      type: 'string',
      required: true,
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['copy', 'delete', 'download', 'get', 'rename', 'share']
        }
      },
      description: 'Select the file',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFiles',
          dependsOn: ['browseFolderId'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Copy: Destination Folder ID (dynamic dropdown)
    {
      key: 'destinationFolderId',
      label: 'Destination Folder',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['copy']
        }
      },
      placeholder: 'Select a folder (optional)',
      description: 'Select the folder to copy the file to. Leave empty to copy to same location.',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Copy/Rename: New File Name
    {
      key: 'newFileName',
      label: 'New File Name',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['copy']
        }
      },
      description: 'New name for the copied file. Leave empty to keep original name.'
    },

    // Rename: New Name
    {
      key: 'newName',
      label: 'New Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['rename']
        }
      },
      description: 'New name for the file'
    },

    // Search: Query
    {
      key: 'searchQuery',
      label: 'Search Query',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['file', 'folder'],
          operation: ['search']
        }
      },
      description: 'Search query (matches filename, metadata, and content)'
    },

    // Share: Link Type
    {
      key: 'linkType',
      label: 'Link Type',
      type: 'options',
      default: 'view',
      displayOptions: {
        show: {
          resource: ['file', 'folder'],
          operation: ['share']
        }
      },
      options: [
        { value: 'view', name: 'View', description: 'Read-only access' },
        { value: 'edit', name: 'Edit', description: 'Read and write access' },
        { value: 'embed', name: 'Embed', description: 'Embeddable link (view only)' }
      ],
      description: 'Type of sharing link to create'
    },

    // Share: Link Scope
    {
      key: 'linkScope',
      label: 'Link Scope',
      type: 'options',
      default: 'anonymous',
      displayOptions: {
        show: {
          resource: ['file', 'folder'],
          operation: ['share']
        }
      },
      options: [
        { value: 'anonymous', name: 'Anyone', description: 'Anyone with the link can access' },
        { value: 'organization', name: 'Organization', description: 'Only people in the organization can access' }
      ],
      description: 'Who can use the sharing link'
    },

    // Upload: Parent Folder selection (dynamic dropdown)
    {
      key: 'parentFolderId',
      label: 'Upload To Folder',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['upload']
        }
      },
      placeholder: 'Select a folder (optional)',
      description: 'Select the folder to upload to. Leave empty for root folder.',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Upload: File Name
    {
      key: 'fileName',
      label: 'File Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['upload']
        }
      },
      description: 'Name of the file to upload (e.g., document.pdf)'
    },

    // Upload: File Content - supports file picker or base64 text input
    {
      key: 'contentBase64',
      label: 'File Content',
      type: 'text',
      required: true,
      customComponent: 'FileUpload',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['upload']
        }
      },
      description: 'Upload a file or paste Base64-encoded content. Use {{ }} expressions to reference upstream node output.'
    },

    // MIME Type (auto-detected from file upload, or inferred from fileName on backend)
    {
      key: 'mimeType',
      label: 'MIME Type',
      type: 'hidden',
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['upload']
        }
      },
      description: 'Auto-detected MIME type of the uploaded file'
    },

    // ===============================
    // FOLDER PROPERTIES
    // ===============================

    // Folder selection (dynamic dropdown for delete/rename/share operations)
    {
      key: 'folderId',
      label: 'Folder',
      type: 'string',
      required: true,
      default: '',
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['delete', 'rename', 'share']
        }
      },
      description: 'Select the folder',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Get Children: Folder selection (dynamic dropdown)
    {
      key: 'folderId',
      label: 'Folder',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['getChildren']
        }
      },
      placeholder: 'Select a folder (optional)',
      description: 'Select the folder to list contents from. Leave empty for root folder.',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Create: Folder Name
    {
      key: 'folderName',
      label: 'Folder Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['create']
        }
      },
      description: 'Name of the new folder. Use "/" to create nested folders (e.g., "folder1/folder2").'
    },

    // Create: Parent Folder selection (dynamic dropdown)
    {
      key: 'parentFolderId',
      label: 'Parent Folder',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['create']
        }
      },
      placeholder: 'Select a folder (optional)',
      description: 'Select the parent folder. Leave empty to create in root.',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Rename: New Name
    {
      key: 'newName',
      label: 'New Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['rename']
        }
      },
      description: 'New name for the folder'
    },

    // ===============================
    // COMMON PROPERTIES
    // ===============================

    // Return All
    {
      key: 'returnAll',
      label: 'Return All',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['file', 'folder'],
          operation: ['search', 'getChildren']
        }
      },
      description: 'Return all results or limit the number'
    },

    // Limit
    {
      key: 'limit',
      label: 'Limit',
      type: 'number',
      default: 50,
      displayOptions: {
        show: {
          resource: ['file', 'folder'],
          operation: ['search', 'getChildren'],
          returnAll: [false]
        }
      },
      description: 'Maximum number of results to return'
    },

    // Simplify Output
    {
      key: 'simplifyOutput',
      label: 'Simplify Output',
      type: 'boolean',
      default: true,
      displayOptions: {
        show: {
          resource: ['file', 'folder'],
          operation: ['get', 'search', 'getChildren']
        }
      },
      description: 'Return a simplified version of the response'
    },

    // === DOWNLOAD OPTIONS ===
    {
      key: 'decodeTextContent',
      label: 'Decode Text Content',
      type: 'boolean',
      default: false,
      description: 'Automatically decode text files (CSV, JSON, TXT, XML) to readable text. When enabled, returns "content" as text in addition to contentBase64.',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['download']
        }
      }
    },
    {
      key: 'parseJsonContent',
      label: 'Parse JSON Content',
      type: 'boolean',
      default: false,
      description: 'Automatically parse JSON files into an object/array. When enabled, returns "json" as the parsed data. This is faster than using a Code node for large JSON files.',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['download'],
          decodeTextContent: [true]
        }
      }
    }
  ]
};

export default schema;
